import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import styledComponent from '../../../utils/styledComponent';
import theme from '../../../theme';
import Button from '../../../components/atoms/forms/Button';
import SelectAllCheckbox from '../../../components/atoms/forms/SelectAllCheckbox';
import DispatchStopRow, { DISPATCH_ROW_COLUMNS } from './DispatchStopRow';
import { isDispatchSelectable, isUnreachableStatus } from './dispatchStops';
import { getStopRowKey } from '../RouteOperations/utils';
import routeCardBase from '../routeCardStyles';

const cardBase = routeCardBase('drc');

const StyledDiv = styledComponent('div', {
  ...cardBase,
  '& .drc-selected.is-armed': {
    color: theme.colors.primary,
    fontWeight: theme.fontWeights.semibold,
  },
  '& .drc-title': {
    ...cardBase['& .drc-title'],
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
    lineHeight: theme.lineHeights.body,
  },
  '& .drc-head-main': {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.fieldGap,
    flex: 1,
    minWidth: 0,
  },
  '& .drc-head-meta': {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.fieldGap,
    marginLeft: 'auto',
    flex: 'none',
  },
  '& .drc-actions': {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.gapMd,
    flex: 'none',
  },
  [`@media (max-width: ${theme.breakpoints.mobile})`]: {
    '& .drc-head-main': {
      flex: '1 1 100%',
    },
    '& .drc-head-meta': {
      flex: '1 1 100%',
      flexWrap: 'wrap',
      justifyContent: 'space-between',
      marginLeft: 0,
    },
    '& .drc-actions': {
      flex: '1 1 100%',
    },
    '& .drc-actions > *': {
      flex: 1,
    },
  },
  '& .drc-counts': {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.labelGap,
    lineHeight: theme.lineHeights.body,
  },
  '& .drc-columns': {
    display: 'grid',
    gridTemplateColumns: DISPATCH_ROW_COLUMNS,
    gap: theme.spacing.fieldGap,
    padding: theme.spacing.cellPadding,
    background: theme.colors.bgMuted,
    borderBottom: `1px solid ${theme.colors.borderLight}`,
    fontSize: theme.sizes.smallFontSize,
    fontWeight: theme.fontWeights.semibold,
    color: theme.colors.textMuted,
    textTransform: 'uppercase',
    letterSpacing: theme.typography.microLabel.letterSpacing,
  },
  '& .drc-columns > :last-child': {
    textAlign: 'right',
  },
});

const stopOrder = (stop) => {
  const n = Number.parseFloat(stop.stop);
  return Number.isNaN(n) ? Number.MAX_SAFE_INTEGER : n;
};

function DispatchRouteCard({
  routeName,
  stops,
  selectedKeys,
  disabled,
  onToggleStop,
  onToggleMany,
  onMarkRoute,
}) {
  const rows = useMemo(
    () =>
      [...stops]
        .sort(
          (a, b) =>
            stopOrder(a) - stopOrder(b) ||
            (a.stockingIndex || 0) - (b.stockingIndex || 0),
        )
        .map((stop) => ({
          stop,
          stopKey: getStopRowKey(stop),
          selectable: isDispatchSelectable(stop),
        })),
    [stops],
  );
  const selectableKeys = useMemo(
    () => rows.filter((r) => r.selectable).map((r) => r.stopKey),
    [rows],
  );
  const selectedInRoute = selectableKeys.filter((k) => selectedKeys.has(k));
  const selectedCount = selectedInRoute.length;
  const unreachableCount = rows.filter(({ stop }) =>
    isUnreachableStatus(stop.derivedStatus && stop.derivedStatus.status),
  ).length;
  const driverStop = stops.find((s) => s.driverEmail);
  const driverEmail = driverStop ? driverStop.driverEmail : '';

  return (
    <StyledDiv data-testid={`dispatch-route-card-${routeName}`}>
      <div className="drc-header">
        <div className="drc-head-main">
          <SelectAllCheckbox
            selectedCount={selectedCount}
            totalCount={selectableKeys.length}
            disabled={disabled}
            onChange={(checked) => onToggleMany(selectableKeys, checked)}
            subject={`stops on ${routeName}`}
            size="sm"
            layout="stacked"
            title={
              selectableKeys.length === 0
                ? 'Every stop on this route is already stocked or unreachable'
                : undefined
            }
            dataTestId={`dispatch-route-select-all-${routeName}`}
          />
          <span className="drc-title" title={routeName}>
            {routeName}
          </span>
          {driverEmail && (
            <span className="drc-stat" title={driverEmail}>
              {driverEmail}
            </span>
          )}
        </div>
        <div className="drc-head-meta">
          <span className="drc-counts">
            <span className="drc-stat">
              <strong>{stops.length}</strong> stops
            </span>
            <span className="drc-stat">
              <strong>{unreachableCount}</strong> unreachable
            </span>
          </span>
          <span
            className={`drc-stat drc-selected${
              selectedCount > 0 ? ' is-armed' : ''
            }`}
            data-testid={`dispatch-route-selected-${routeName}`}
          >
            {`${selectedCount} selected`}
          </span>
          <div className="drc-actions">
            <Button
              variant="info-outline"
              onClick={() => onToggleMany(selectedInRoute, false)}
              disabled={selectedCount === 0}
              dataTestId={`dispatch-route-clear-${routeName}`}
            >
              Clear
            </Button>
            <Button
              variant="danger-outline"
              onClick={() => onMarkRoute(routeName)}
              disabled={disabled || selectedCount === 0}
              dataTestId={`dispatch-route-mark-${routeName}`}
            >
              Mark unreachable
            </Button>
          </div>
        </div>
      </div>
      <div className="drc-columns" aria-hidden="true">
        <span />
        <span title="Position of the stop in the route's stocking order">
          Stop #
        </span>
        <span>Location</span>
        <span>Status</span>
      </div>
      {rows.map(({ stop, stopKey }) => (
        <DispatchStopRow
          key={stopKey}
          stop={stop}
          stopKey={stopKey}
          checked={selectedKeys.has(stopKey)}
          disabled={disabled}
          onToggle={onToggleStop}
        />
      ))}
    </StyledDiv>
  );
}

DispatchRouteCard.propTypes = {
  routeName: PropTypes.string.isRequired,
  stops: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      driverEmail: PropTypes.string,
      derivedStatus: PropTypes.shape({ status: PropTypes.string }),
    }),
  ).isRequired,
  selectedKeys: PropTypes.instanceOf(Set).isRequired,
  disabled: PropTypes.bool,
  onToggleStop: PropTypes.func.isRequired,
  onToggleMany: PropTypes.func.isRequired,
  onMarkRoute: PropTypes.func.isRequired,
};

DispatchRouteCard.defaultProps = {
  disabled: false,
};

export default DispatchRouteCard;
